<script lang="ts">
  import { onMount } from "svelte";
  import {
    fetchLogs,
    listAuditEvents,
    listUsers,
    listSchools,
    listDevicesLogs,
    ApiError,
  } from "./api";
  import Autocomplete from "./Autocomplete.svelte";
  import { push, link } from "svelte-spa-router";
  import UserLink from "./UserLink.svelte";
  import type { AuditEntry, AuditLevel } from "@diy-avatar/shared/admin";

  let entries: AuditEntry[] = [];
  let nextCursor: number | null = null;
  let total = 0;
  let limit = 50;
  let loading = false;
  let error: string | null = null;

  // Server-side filters (the backend now scopes these in /admin/logs).
  let qFilter = ""; // -> q (free text)
  let eventFilter = ""; // -> event (EXACT)
  let fromFilter = ""; // <input type=date> -> from
  let toFilter = ""; // <input type=date> -> to
  let userFilter = ""; // -> user
  let deviceFilter = ""; // -> device (label substring)
  let schoolFilter = ""; // -> school (school id)
  let roomFilter = ""; // -> room (room id); set only via row icon / chip
  // Level checkboxes -> comma-joined `level` param.
  const LEVELS: AuditLevel[] = ["info", "warn", "error"];
  let levelSel: Record<AuditLevel, boolean> = { info: false, warn: false, error: false };
  $: levelFilter = LEVELS.filter((l) => levelSel[l]).join(",");

  // Autocomplete + event-select source data (best-effort; empty on failure).
  let eventOptions: string[] = [];
  let userItems: { value: string; label: string }[] = [];
  let deviceItems: { value: string; label: string }[] = [];
  let schoolItems: { value: string; label: string }[] = [];

  async function loadSources() {
    try {
      const res = await listAuditEvents();
      eventOptions = res.events;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        push("/");
        return;
      }
    }
    try {
      const res = await listUsers();
      userItems = res.users.map((u) => ({
        value: u.email,
        label: u.display_name ? `${u.display_name} <${u.email}>` : u.email,
      }));
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        push("/");
        return;
      }
    }
    try {
      const res = await listDevicesLogs();
      deviceItems = res.devices.map((d) => ({ value: d.label, label: d.label }));
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        push("/");
        return;
      }
    }
    try {
      const res = await listSchools();
      schoolItems = res.schools.map((s) => ({ value: String(s.id), label: s.name }));
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        push("/");
        return;
      }
    }
  }

  /** Convert a yyyy-mm-dd date input to epoch ms (UTC); "" when empty. */
  function dateToMs(v: string): number | undefined {
    if (!v) return undefined;
    const ms = Date.parse(v);
    return Number.isNaN(ms) ? undefined : ms;
  }

  async function load(reset = false) {
    loading = true;
    error = null;
    try {
      const res = await fetchLogs({
        limit,
        since: reset ? undefined : nextCursor ?? undefined,
        from: dateToMs(fromFilter),
        to: dateToMs(toFilter),
        user: userFilter || undefined,
        q: qFilter || undefined,
        event: eventFilter || undefined,
        device: deviceFilter || undefined,
        school: schoolFilter || undefined,
        room: roomFilter || undefined,
        level: levelFilter || undefined,
      });
      total = res.total;
      if (reset) {
        entries = res.entries;
      } else {
        entries = [...entries, ...res.entries];
      }
      nextCursor = res.next_cursor;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        push("/");
        return;
      }
      error = err instanceof Error ? err.message : "Could not load logs.";
    } finally {
      loading = false;
    }
  }

  /** Reset the cursor and reload from the top whenever filters change. */
  function applyFilters() {
    nextCursor = null;
    void load(true);
  }

  // Active-filter chips: a removable summary of every set filter.
  type ChipKey =
    | "event"
    | "q"
    | "user"
    | "device"
    | "school"
    | "room"
    | "level"
    | "from"
    | "to";

  $: chips = (() => {
    const out: { key: ChipKey; text: string }[] = [];
    if (eventFilter) out.push({ key: "event", text: `event: ${eventFilter}` });
    if (qFilter) out.push({ key: "q", text: `search: ${qFilter}` });
    if (userFilter) out.push({ key: "user", text: `user: ${userFilter}` });
    if (deviceFilter) out.push({ key: "device", text: `device: ${deviceFilter}` });
    if (schoolFilter) out.push({ key: "school", text: `school: ${schoolFilter}` });
    if (roomFilter) out.push({ key: "room", text: `room: ${roomFilter}` });
    if (levelFilter) out.push({ key: "level", text: `level: ${levelFilter}` });
    if (fromFilter) out.push({ key: "from", text: `from: ${fromFilter}` });
    if (toFilter) out.push({ key: "to", text: `to: ${toFilter}` });
    return out;
  })();

  function clearChip(key: ChipKey) {
    if (key === "event") eventFilter = "";
    else if (key === "q") qFilter = "";
    else if (key === "user") userFilter = "";
    else if (key === "device") deviceFilter = "";
    else if (key === "school") schoolFilter = "";
    else if (key === "room") roomFilter = "";
    else if (key === "from") fromFilter = "";
    else if (key === "to") toFilter = "";
    else if (key === "level") {
      levelSel = { info: false, warn: false, error: false };
    }
    applyFilters();
  }

  // Row filter-icon handlers: set the matching filter then reload.
  function filterUser(e: AuditEntry) {
    userFilter = String(e.username ?? e.user_id ?? "");
    applyFilters();
  }
  function filterTarget(e: AuditEntry) {
    userFilter = String(e.target_username ?? e.target_user_id ?? "");
    applyFilters();
  }
  function filterDevice(e: AuditEntry) {
    deviceFilter = e.device_id ?? "";
    applyFilters();
  }
  function filterSchool(e: AuditEntry) {
    schoolFilter = String(e.school_id ?? "");
    applyFilters();
  }
  function filterRoom(e: AuditEntry) {
    roomFilter = String(e.room_id ?? "");
    applyFilters();
  }

  onMount(() => {
    void loadSources();
    void load(true);
  });
</script>

<div class="controls">
  <label>
    Event
    <select bind:value={eventFilter} on:change={applyFilters} data-testid="filter-event">
      <option value="">all events</option>
      {#each eventOptions as ev}
        <option value={ev}>{ev}</option>
      {/each}
    </select>
  </label>
  <label>
    Search (event/payload)
    <input
      type="text"
      bind:value={qFilter}
      on:change={applyFilters}
      data-testid="filter"
      placeholder="free text"
    />
  </label>
  <label>
    From
    <input type="date" bind:value={fromFilter} on:change={applyFilters} data-testid="filter-from" />
  </label>
  <label>
    To
    <input type="date" bind:value={toFilter} on:change={applyFilters} data-testid="filter-to" />
  </label>
  <label for="ac-user">
    User
    <Autocomplete
      id="ac-user"
      items={userItems}
      bind:value={userFilter}
      placeholder="email or id"
      testid="filter-user"
      on:change={applyFilters}
    />
  </label>
  <label for="ac-device">
    Device
    <Autocomplete
      id="ac-device"
      items={deviceItems}
      bind:value={deviceFilter}
      placeholder="device label"
      testid="filter-device"
      on:change={applyFilters}
    />
  </label>
  <label for="ac-school">
    School
    <Autocomplete
      id="ac-school"
      items={schoolItems}
      bind:value={schoolFilter}
      placeholder="school"
      testid="filter-school"
      on:change={applyFilters}
    />
  </label>
  <fieldset class="levels" data-testid="filter-level">
    <legend>Level</legend>
    {#each LEVELS as lvl}
      <label class="lvl">
        <input type="checkbox" bind:checked={levelSel[lvl]} on:change={applyFilters} />
        {lvl}
      </label>
    {/each}
  </fieldset>
  <label>
    Page size
    <select bind:value={limit} on:change={applyFilters} data-testid="limit">
      <option value={25}>25</option>
      <option value={50}>50</option>
      <option value={100}>100</option>
      <option value={250}>250</option>
    </select>
  </label>
  <button on:click={applyFilters} data-testid="reload">Reload</button>
  <span class="count" data-testid="count">{entries.length} / {total}</span>
</div>
{#if chips.length > 0}
  <div class="chips" data-testid="filter-chips">
    {#each chips as c (c.key)}
      <button
        type="button"
        class="chip"
        data-testid="chip-{c.key}"
        on:click={() => clearChip(c.key)}
        aria-label="Clear filter {c.text}"
      >
        {c.text} ×
      </button>
    {/each}
  </div>
{/if}
{#if error}
  <p class="error" data-testid="error">{error}</p>
{/if}
<table data-testid="logs-table">
  <thead>
    <tr>
      <th>id</th>
      <th>ts</th>
      <th>level</th>
      <th>user</th>
      <th>target</th>
      <th>device</th>
      <th>school</th>
      <th>room</th>
      <th>event</th>
      <th>payload</th>
    </tr>
  </thead>
  <tbody>
    {#each entries as e (e.id)}
      <tr>
        <td>{e.id}</td>
        <td>{new Date(e.ts).toISOString()}</td>
        <td>
          <span class="lvl-badge lvl-{e.level ?? 'info'}">{e.level ?? "info"}</span>
        </td>
        <td>
          {#if e.user_id != null}
            <UserLink id={e.user_id} email={e.username} displayName={e.username} size={20} />
            <button
              type="button"
              class="filter-icon"
              aria-label="Filter by this user"
              on:click={() => filterUser(e)}
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" /></svg>
            </button>
          {:else}—{/if}
        </td>
        <td>
          {#if e.target_user_id != null}
            <UserLink id={e.target_user_id} email={e.target_username} displayName={e.target_username} size={20} />
            <button
              type="button"
              class="filter-icon"
              aria-label="Filter by this target user"
              on:click={() => filterTarget(e)}
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" /></svg>
            </button>
          {:else}—{/if}
        </td>
        <td>
          {#if e.device_id}
            <a href="/devices/{encodeURIComponent(e.device_id)}" use:link>{e.device_id}</a>
            <button
              type="button"
              class="filter-icon"
              aria-label="Filter by this device"
              on:click={() => filterDevice(e)}
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" /></svg>
            </button>
          {:else}—{/if}
        </td>
        <td>
          {#if e.school_id != null}
            <a href="/schools/{e.school_id}" use:link>{e.school_name ?? e.school_id}</a>
            <button
              type="button"
              class="filter-icon"
              aria-label="Filter by this school"
              on:click={() => filterSchool(e)}
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" /></svg>
            </button>
          {:else}—{/if}
        </td>
        <td>
          {#if e.room_id != null}
            <a href="/rooms/{e.room_id}" use:link>{e.room_name ?? e.room_id}</a>
            <button
              type="button"
              class="filter-icon"
              aria-label="Filter by this room"
              on:click={() => filterRoom(e)}
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" /></svg>
            </button>
          {:else}—{/if}
        </td>
        <td>{e.event}</td>
        <td><code>{JSON.stringify(e.payload)}</code></td>
      </tr>
    {/each}
    {#if entries.length === 0 && !loading}
      <tr><td colspan="10" class="empty" data-testid="empty">No entries.</td></tr>
    {/if}
  </tbody>
</table>
{#if nextCursor !== null && entries.length > 0}
  <button on:click={() => load(false)} disabled={loading} data-testid="load-more">
    {loading ? "Loading…" : "Load more"}
  </button>
{/if}

<style>
  .controls {
    display: flex;
    gap: 1rem;
    align-items: end;
    flex-wrap: wrap;
    margin-bottom: 0.5rem;
  }
  label {
    display: grid;
    gap: 0.2rem;
    font-size: 0.85rem;
    color: #d1d5db;
  }
  input,
  select {
    padding: 0.4rem 0.5rem;
    background: #0f172a;
    color: #f3f4f6;
    border: 1px solid #1f2937;
    border-radius: 4px;
  }
  button {
    padding: 0.4rem 0.7rem;
    background: #1f2937;
    color: #f3f4f6;
    border: 1px solid #374151;
    border-radius: 4px;
  }
  .count {
    color: #9ca3af;
    align-self: center;
  }
  .chips {
    display: flex;
    gap: 0.4rem;
    flex-wrap: wrap;
    margin-bottom: 0.6rem;
  }
  .chip {
    padding: 0.15rem 0.5rem;
    background: #1f2937;
    color: #d1d5db;
    border: 1px solid #374151;
    border-radius: 999px;
    font-size: 0.78rem;
    cursor: pointer;
  }
  .chip:hover {
    background: #374151;
  }
  .filter-icon {
    background: transparent;
    border: none;
    padding: 0 0.25rem;
    color: currentColor;
    cursor: pointer;
    vertical-align: middle;
    line-height: 0;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    background: #111827;
  }
  th,
  td {
    padding: 0.4rem 0.6rem;
    text-align: left;
    border-bottom: 1px solid #1f2937;
    font-size: 0.85rem;
  }
  th {
    background: #0f172a;
    color: #9ca3af;
  }
  code {
    color: #93c5fd;
  }
  td a {
    color: #34d399;
    text-decoration: none;
  }
  td a:hover {
    text-decoration: underline;
  }
  .levels {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    border: 1px solid #1f2937;
    border-radius: 4px;
    padding: 0.2rem 0.5rem;
    margin: 0;
  }
  .levels legend {
    font-size: 0.85rem;
    color: #d1d5db;
    padding: 0 0.3rem;
  }
  .levels .lvl {
    display: inline-flex;
    flex-direction: row;
    align-items: center;
    gap: 0.25rem;
    color: #d1d5db;
    font-size: 0.85rem;
  }
  .lvl-badge {
    display: inline-block;
    padding: 0.1rem 0.4rem;
    border-radius: 4px;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
  }
  .lvl-info {
    background: #0c4a6e;
    color: #7dd3fc;
  }
  .lvl-warn {
    background: #3b2f06;
    color: #fbbf24;
  }
  .lvl-error {
    background: #4c0519;
    color: #fca5a5;
  }
  .empty {
    color: #6b7280;
    text-align: center;
  }
  .error {
    color: #fca5a5;
  }
</style>
